import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { LoadingPage } from '@/components/ui/loading-page';
import { updateProgram } from '@/lib/api/program';
import { fetchProgramById } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { MultiStepProgramForm } from '@/components/programs/MultiStepProgramForm';
import type { ProgramForm as ProgramFormType, APICompatibleProgram, DeliveryMode } from '@/types/program';

const EditProgram = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [programData, setProgramData] = useState<ProgramFormType | null>(null);
  const { user } = useAuth();

  // Load program data on component mount
  useEffect(() => {
    if (!id) {
      toast.error("Program ID is required");
      navigate("/programs");
      return;
    }

    loadProgramData();
  }, [id]);

  // Check authentication
  useEffect(() => {
    if (user === null) {
      toast.error("Please log in to edit programs");
      navigate("/login");
    }
  }, [user, navigate]);

  const loadProgramData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchProgramById(id!);
      
      if (response.success && response.data) {
        // Transform API data to form format
        const transformedData = transformApiDataToFormFormat(response.data);
        
        
        // Validate transformed data
        if (!transformedData.title) {
          console.warn('Transformed data missing required fields:', transformedData);
          setError("Program data is incomplete or corrupted");
          toast.error("Program data is incomplete or corrupted");
          return;
        }
        
        console.log('Transformed program data:', transformedData);
        console.log('Program comparison in transformed data:', transformedData.programComparison);
        setProgramData(transformedData);
      } else if (response.statusCode === 200 && response.data) {
        // Handle alternative response format
        const transformedData = transformApiDataToFormFormat(response.data);
        
        // Validate transformed data
        if (!transformedData.title) {
          console.warn('Transformed data missing required fields:', transformedData);
          setError("Program data is incomplete or corrupted");
          toast.error("Program data is incomplete or corrupted");
          return;
        }
        
        setProgramData(transformedData);
      } else {
        setError("Failed to load program data");
        toast.error("Failed to load program data");
      }
    } catch (error: any) {
      console.error('EditProgram: Error loading program:', error);
      
      // Handle different types of errors
      if (error.statusCode === 401) {
        setError("Authentication required. Please log in again.");
        toast.error("Authentication required. Please log in again.");
      } else if (error.statusCode === 403) {
        setError("You don't have permission to access this program.");
        toast.error("You don't have permission to access this program.");
      } else if (error.statusCode === 404) {
        setError("Program not found. Please check the URL.");
        toast.error("Program not found. Please check the URL.");
      } else if (error.message) {
        setError(error.message);
        toast.error(error.message);
      } else {
        setError("Failed to load program data. Please try again.");
        toast.error("Failed to load program data. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Transform API data to form format
  const transformApiDataToFormFormat = (apiProgram: any): ProgramFormType => {
    // Validate input
    if (!apiProgram || typeof apiProgram !== 'object') {
      console.warn('Invalid API program data provided to transformApiDataToFormFormat');
      return {} as ProgramFormType;
    }

    // Extract current program type data from programTypeDataMap
    const currentProgramType = apiProgram.programType || 'hybrid';
    const programTypeDataMap = apiProgram.programTypeDataMap || {};
    const currentTypeData = programTypeDataMap[currentProgramType] || apiProgram;

    // Helper function to safely extract category ID
    const getCategoryId = (categoryData: any): string => {
      if (typeof categoryData === 'string') return categoryData;
      if (typeof categoryData === 'object' && categoryData?._id) return categoryData._id;
      return '';
    };

    // Helper function to transform program comparison
    const transformProgramComparison = (comparisonData: any) => {
      if (!comparisonData) {
        return { showComparisonTable: false, comparisonCategories: [] };
      }

      // The API data already has the correct structure, just ensure it has the required fields
      return {
        showComparisonTable: comparisonData.showComparisonTable || false,
        comparisonCategories: (comparisonData.comparisonCategories || []).map((category: any, index: number) => ({
          id: category.id || `category_${index}`,
          name: category.name || 'Program Features',
          features: (category.features || []).map((feature: any, featureIndex: number) => ({
            id: feature.id || feature._id || `feature_${index}_${featureIndex}`,
            name: feature.name || '',
            included: feature.included || false,
            includedInOther: feature.includedInOther || false,
            otherNote: feature.otherNote || ''
          }))
        }))
      };
    };

    // Helper function to transform schedule data for each program type
    const transformScheduleData = (scheduleData: any, programType: string) => {
      if (!scheduleData) return {};

      // Handle hybrid schedule with enrollment periods
      if (programType === 'hybrid') {
        // If enrollmentPeriods exist, use them; otherwise create from startMonth/startDate
        let enrollmentPeriods = scheduleData.enrollmentPeriods || [];
        
        // If no enrollmentPeriods but we have startMonth/startDate, create them
        if (enrollmentPeriods.length === 0 && (scheduleData.startMonth?.length > 0 || scheduleData.startDate?.length > 0)) {
          const monthYearMap = new Map();
          
          // Group dates by month/year
          scheduleData.startDate?.forEach((dateStr: string) => {
            if (dateStr) {
              const date = new Date(dateStr);
              const month = date.toLocaleString('default', { month: 'long' });
              const year = date.getFullYear();
              const key = `${month}-${year}`;
              
              if (!monthYearMap.has(key)) {
                monthYearMap.set(key, { month, year, dates: [] });
              }
              monthYearMap.get(key).dates.push(dateStr);
            }
          });
          
          // Convert to enrollment periods
          enrollmentPeriods = Array.from(monthYearMap.values()).map((data, index) => ({
            id: `${data.month}-${data.year}-${Date.now()}-${index}`,
            month: data.month,
            year: data.year,
            dates: data.dates,
            isActive: true
          }));
        }

        return {
          hybrid: {
            startMonth: scheduleData.startMonth || [],
            startDate: scheduleData.startDate || [],
            clinicalLocations: scheduleData.hybrid?.clinicalLocations || [],
            practiceHours: scheduleData.hybrid?.practiceHours || 0,
            capacity: scheduleData.hybrid?.capacity || 0,
            facilityRequirements: scheduleData.hybrid?.facilityRequirements || [],
            enrollmentPeriods: enrollmentPeriods,
          }
        };
      }

      // Handle batch-based schedules (via-zoom, on-campus)
      if (programType === 'via-zoom' || programType === 'on-campus') {
        // Check if there's type-specific batch data first, then fall back to root batches
        const typeSpecificBatches = scheduleData[programType]?.batches || scheduleData.batches || [];
        
        return {
          [programType]: {
            batches: typeSpecificBatches.map((batch: any) => ({
              schedule: (batch.schedule || []).map((s: any) => {
                // Parse combined date string (e.g., "Monday - Saturday" -> startDay/endDay)
                let startDay = '', endDay = '';
                if (s.date && s.date.includes(' - ')) {
                  [startDay, endDay] = s.date.split(' - ');
                } else if (s.date) {
                  startDay = s.date;
                }
                
                // Parse combined time string (e.g., "7:00 AM - 7:00 PM" -> startTime/endTime)
                let startTime = '', endTime = '';
                if (s.time && s.time.includes(' - ')) {
                  [startTime, endTime] = s.time.split(' - ');
                } else if (s.time) {
                  startTime = s.time;
                }
                
                return {
                  batch: s.batch || "",
                  date: s.date || "",
                  time: s.time || "",
                  startDay: startDay,
                  endDay: endDay,
                  startTime: startTime,
                  endTime: endTime,
                  capacity: s.capacity,
                  minParticipants: s.minParticipants,
                  maxParticipants: s.maxParticipants,
                };
              }),
            })),
            facilityRequirements: scheduleData[programType]?.facilityRequirements || [],
            clinicalLocations: scheduleData[programType]?.clinicalLocations || [],
            practiceHours: scheduleData[programType]?.practiceHours || 0,
            capacity: scheduleData[programType]?.capacity || 0,
            minParticipants: scheduleData[programType]?.minParticipants || 0,
            maxParticipants: scheduleData[programType]?.maxParticipants || 0,
          }
        };
      }

      // Handle online schedule
      if (programType === 'online') {
        return {
          online: {
            startMonth: scheduleData.startMonth || [],
            startDate: scheduleData.startDate || [],
            enrollmentPeriods: scheduleData.enrollmentPeriods || [],
          }
        };
      }

      return {};
    };

    // Helper function to get delivery label
    const getDeliveryLabel = (programType: string): string => {
      const deliveryMap = {
        'hybrid': 'Hybrid',
        'via-zoom': 'Via-Zoom',
        'on-campus': 'On-Campus',
        'online': 'Online'
      };
      return deliveryMap[programType as keyof typeof deliveryMap] || programType;
    };

    // Transform modules to ensure proper structure
    const transformModules = (modules: any[]) => {
      if (!Array.isArray(modules)) return [];
      
      return modules.map((module: any) => ({
        label: module.label || 'Module',
        isActive: module.isActive !== false,
        order: module.order || 0,
        items: (module.items || []).map((item: any) => ({
          id: item.id || item._id || 'item-1',
          title: item.title || 'Module Item',
          subtitle: item.subtitle || '',
          isExam: item.isExam || false,
          price: item.price || 0,
          _id: item._id
        }))
      }));
    };

    return {
      // Basic information
      // Note: subtitle, videoUrl, about, nationalCertifications, podcastData moved to program types
      title: apiProgram.title || "",
      language: apiProgram.language || "english",
      status: apiProgram.status || "active",
      categoryId: getCategoryId(apiProgram.categoryId),
      programType: currentProgramType,
      defaultProgramType: apiProgram.defaultProgramType || currentProgramType,
      availableProgramTypes: Object.keys(programTypeDataMap).filter(type => {
        const typeData = programTypeDataMap[type];
        const hasPaymentOptions = typeData.paymentOptions && typeData.paymentOptions.length > 0;
        const hasSchedule = typeData.schedule && Object.keys(typeData.schedule).length > 0;
        const hasContent = (typeData.modules && typeData.modules.length > 0) || 
                          (typeData.keyFeatures && typeData.keyFeatures.length > 0);
        return hasPaymentOptions || hasSchedule || hasContent ||
               (typeData.title && typeData.title.trim() !== '') ||
               (typeData.about && typeData.about.trim() !== '');
      }) as DeliveryMode[],
      // Note: about removed - now per program type
      tagline: apiProgram.tagline || "",
      
      // Content data - use current type data first, fallback to main program data
      modules: transformModules(currentTypeData.modules || apiProgram.modules || []),
      keyFeatures: currentTypeData.keyFeatures || apiProgram.keyFeatures || [],
      
      // Audience data
      thisCourseData: currentTypeData.thisCourseData || apiProgram.thisCourseData || [],
      whoisthisFor: currentTypeData.whoisthisFor || apiProgram.whoisthisFor || [],
      wherecaniWork: currentTypeData.wherecaniWork || apiProgram.wherecaniWork || [],
      clinicalProcedures: currentTypeData.clinicalProcedures || apiProgram.clinicalProcedures || [],
      clinicalProceduresGrouped: currentTypeData.clinicalProceduresGrouped || apiProgram.clinicalProceduresGrouped || [],
      // Note: nationalCertifications moved to program types
      workPlaces: currentTypeData.workPlaces || apiProgram.workPlaces || [],
      
      // Marketing data
      socialMedia: currentTypeData.socialMedia || apiProgram.socialMedia || {
        facebook: null,
        twitter: null,
        instagram: null,
        linkedin: null,
        youtube: null,
        website: null,
      },
      faqs: currentTypeData.faqs || apiProgram.faqs || [],
      // Note: podcastData moved to program types
      programComparison: transformProgramComparison(apiProgram.programComparison),
      
      // Premium fields
      marketingTags: Array.isArray(apiProgram.marketingTags)
        ? apiProgram.marketingTags
        : [],
      premiumDescription: apiProgram.premiumDescription,
      premiumTagline: apiProgram.premiumTagline,
      premiumRating: apiProgram.premiumRating,
      premiumReviewCount: apiProgram.premiumReviewCount,
      premiumStudentCount: apiProgram.premiumStudentCount,
      premiumViewCount: apiProgram.premiumViewCount,
      premiumLikeCount: apiProgram.premiumLikeCount,
      premiumShareCount: apiProgram.premiumShareCount,
      
      // Configuration fields
      duration: apiProgram.duration,
      platform: apiProgram.platform,
      payAsYouGo: apiProgram.payAsYouGo || false,
      moduleConfiguration: apiProgram.moduleConfiguration,
      modulePricingMetadata: apiProgram.modulePricingMetadata,
      breadcrumb: apiProgram.breadcrumb || [],
      
      // Type-specific data
      typeSpecificData: {
        programType: currentProgramType,
        delivery: getDeliveryLabel(currentProgramType),
        paymentOptions: (currentTypeData.paymentOptions || []).map((option: any) => ({
          ...option,
          // Preserve original data, only add minimal fallbacks for truly missing fields
          paymentOptionName: option.paymentOptionName || option.displayName || '',
          currency: option.currency || 'USD',
          youSave: option.youSave || '',
          cardTag: option.cardTag || { available: false }
        })),
        programSchedule: transformScheduleData(currentTypeData.schedule, currentProgramType),
      },
      
      // Multi-type support - ensure all program types have required data
      typeSpecificDataByType: Object.keys(programTypeDataMap).reduce((acc, type) => {
        const typeData = programTypeDataMap[type];
        
        // Check if this program type has meaningful data (not just empty defaults)
        const hasPaymentOptions = typeData.paymentOptions && typeData.paymentOptions.length > 0;
        const hasSchedule = typeData.schedule && Object.keys(typeData.schedule).length > 0;
        const hasContent = (typeData.modules && typeData.modules.length > 0) || 
                          (typeData.keyFeatures && typeData.keyFeatures.length > 0);
        
        // Mark as configured if it has any meaningful data
        const isConfigured = hasPaymentOptions || hasSchedule || hasContent ||
                           (typeData.title && typeData.title.trim() !== '') ||
                           (typeData.about && typeData.about.trim() !== '');
        
        acc[type] = {
          // Basic info
          programType: type,
          delivery: getDeliveryLabel(type),
          title: typeData.title || apiProgram.title || "",
          subtitle: typeData.subtitle || apiProgram.subtitle || "",
          about: typeData.about || apiProgram.about || "",
          categoryId: getCategoryId(apiProgram.categoryId),
          language: typeData.language || apiProgram.language || "english",
          status: typeData.status || apiProgram.status || "active",
          videoUrl: typeData.videoUrl || apiProgram.videoUrl || "",
          platform: typeData.platform || apiProgram.platform || { isIspring: false },
          
          // Content step
          modules: transformModules(typeData.modules || []),
          keyFeatures: typeData.keyFeatures || [],
          payAsYouGo: typeData.payAsYouGo || apiProgram.payAsYouGo || false,
          
          // Audience step
          thisCourseData: typeData.thisCourseData || [],
          whoisthisFor: typeData.whoisthisFor || [],
          wherecaniWork: typeData.wherecaniWork || [],
          clinicalProcedures: typeData.clinicalProcedures || [],
          clinicalProceduresGrouped: typeData.clinicalProceduresGrouped || [],
          nationalCertifications: typeData.nationalCertifications || { isallIncluded: false, items: [] },
          workPlaces: typeData.workPlaces || [],
          forThoseSearching: typeData.forThoseSearching || apiProgram.forThoseSearching || [],
          
          // Pricing step
          paymentOptions: (typeData.paymentOptions || []).map((option: any) => ({
            ...option,
            // Preserve original data, only add minimal fallbacks for truly missing fields
            paymentOptionName: option.paymentOptionName || option.displayName || '',
            currency: option.currency || 'USD',
            youSave: option.youSave || '',
            cardTag: option.cardTag || { available: false }
          })),
          
          // Schedule step
          programSchedule: transformScheduleData(typeData.schedule, type),
          
          // Marketing step
          faqs: typeData.faqs || [],
          podcastData: typeData.podcastData || [],
          socialMedia: typeData.socialMedia || {
            facebook: null,
            twitter: null,
            instagram: null,
            linkedin: null,
            youtube: null,
            website: null,
          },
          
          // Premium details fields
          premiumDescription: typeData.premiumDescription || apiProgram.premiumDescription || "",
          premiumTagline: typeData.premiumTagline || apiProgram.premiumTagline || "",
          premiumRating: typeData.premiumRating || apiProgram.premiumRating || 0,
          premiumReviewCount: typeData.premiumReviewCount || apiProgram.premiumReviewCount || 0,
          premiumStudentCount: typeData.premiumStudentCount || apiProgram.premiumStudentCount || 0,
          premiumViewCount: typeData.premiumViewCount || apiProgram.premiumViewCount || 0,
          premiumLikeCount: typeData.premiumLikeCount || apiProgram.premiumLikeCount || 0,
          premiumShareCount: typeData.premiumShareCount || apiProgram.premiumShareCount || 0,
          
          // Duration field
          duration: typeData.duration || apiProgram.duration || { value: 1, isInWeeks: true },
          
          // Configuration - mark as configured if it has meaningful data
          isConfigured: isConfigured,
        };
        return acc;
      }, {} as any),
    };
  };

  const handleSubmit = async (apiData: APICompatibleProgram) => {
    try {


      // Validate required fields before submission
      if (!apiData.title?.trim()) {
        toast.error("Program title is required");
        return;
      }
      if (!apiData.categoryId) {
        toast.error("Category is required");
        return;
      }
      // Note: 'about' validation removed - now per program type

      // The data is already transformed by MultiStepProgramForm, so we can use it directly
      const apiPayload = {
        ...apiData,
        // Include enhanced module configuration if Pay as you Go is enabled
        ...(apiData.payAsYouGo && apiData.moduleConfiguration && {
          payAsYouGo: apiData.payAsYouGo,
          moduleConfiguration: {
            ...apiData.moduleConfiguration,
            modulePricingEnabled: apiData.payAsYouGo // Automatically enable module pricing when payAsYouGo is true
          },
          ...(apiData.modulePricingMetadata && {
            modulePricingMetadata: apiData.modulePricingMetadata
          })
        })
      };



      // Submit the transformed data to the program API
      const response = await updateProgram(id!, apiPayload);

      if (response.success) {
        
        // Check if premium fields were configured
        const hasPremiumFields = apiPayload.premiumDescription?.trim() || 
                               apiPayload.premiumRating || 
                               apiPayload.premiumReviewCount || 
                               apiPayload.premiumStudentCount || 
                               apiPayload.premiumViewCount || 
                               apiPayload.premiumLikeCount || 
                               apiPayload.premiumShareCount;
        
        if (hasPremiumFields) {
          toast.success("Program updated successfully with premium features!", { duration: 5000 });
        } else {
          toast.success(response.message || "Program updated successfully!", { duration: 5000 });
        }
        
        // Refresh the program data to show updated content
        await loadProgramData();
      } else {
        console.error("Program update failed:", response);
        toast.error(response.message || "Failed to update program");
      }
    } catch (error: any) {
      console.error("Error during program update:", error);
      
      // Handle specific error types
      if (error.message?.includes('Category ID is required')) {
        toast.error("Please select a valid category");
      } else if (error.message?.includes('Payment options are required')) {
        toast.error("Please configure payment options for the program");
      } else if (error.message?.includes('Program schedule is required')) {
        toast.error("Please configure the program schedule");
      } else if (error.message?.includes('Invalid program type')) {
        toast.error("Please select a valid program type");
      } else if (error.details && error.details.length > 0) {
        // Handle validation errors from API (new format)
        const validationErrors = error.details.map((detail: any) => {
          const fieldPath = detail.path?.join(".") || "unknown field";
          return `${fieldPath}: ${detail.message}`;
        });
        
        // Show first few errors in toast
        validationErrors.slice(0, 2).forEach((errorMsg: string) => {
          toast.error(errorMsg);
        });
        
        // Throw error with validation details so form can display them
        throw new Error(`Validation failed: ${validationErrors.join('; ')}`);
      } else if (error.errors && error.errors.length > 0) {
        // Handle validation errors from API (legacy format)
        const validationErrors = error.errors.map((detail: any) => {
          const fieldPath = detail.path?.join(".") || "unknown field";
          return `${fieldPath}: ${detail.message}`;
        });
        
        // Show first few errors in toast
        validationErrors.slice(0, 2).forEach((errorMsg: string) => {
          toast.error(errorMsg);
        });
        
        // Throw error with validation details so form can display them
        throw new Error(`Validation failed: ${validationErrors.join('; ')}`);
      } else if (error.statusCode === 401) {
        toast.error("Authentication required. Please log in again.");
      } else if (error.statusCode === 403) {
        toast.error("You don't have permission to update programs.");
      } else if (error.statusCode === 404) {
        toast.error("Program not found. Please check the URL.");
      } else {
        toast.error(error.message || "Failed to update program");
      }
    }
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Program</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex justify-center gap-4">
              <Button onClick={loadProgramData} variant="outline">
                Retry
              </Button>
              <Button onClick={() => navigate("/programs")} variant="default">
                Back to Programs
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!programData) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-600 mb-4">No Program Data</h1>
            <p className="text-gray-600 mb-6">Unable to load program data for editing.</p>
            <Button onClick={() => navigate("/programs")} variant="default">
              Back to Programs
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold">Edit Program</h1>
              {programData.title && (
                <p className="text-lg font-medium text-blue-600 mt-1">{programData.title}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate("/programs")} 
              variant="outline"
              className="flex items-center gap-2"
            >
              Back to Programs
            </Button>
          </div>
        </div>
        
        <MultiStepProgramForm
          onSubmit={handleSubmit as any}
          isEditMode={true}
          existingData={programData}
        />
      </div>
    </div>
  );
};

export default EditProgram; 